import React, { useMemo, useState } from "react";
import { Avatar, Button, Image, Tooltip } from "antd";
import { CommentOutlined, ForwardOutlined, SmileOutlined, UserOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import type { EmojiClickData } from "emoji-picker-react";
import { useChat } from "./ChatContext";
import { isOwnUploadUrl, resolveAvatarUrl, resolveRealmUrl } from "./zulipClient";
import EmojiPickerPopover from "./EmojiPickerPopover";
import type { ZulipMessage, ZulipReaction, ZulipUser } from "./types";

const EMOJI_SPAN_RE = /(<span[^>]*\bclass="[^"]*\bemoji-([0-9a-f]+(?:-[0-9a-f]+)*)\b[^"]*"[^>]*>)[^<]*(<\/span>)/g;

function codepointsToGlyph(hexCodepoints: string): string | undefined {
  const codepoints = hexCodepoints
    .split("-")
    .map((hex) => parseInt(hex, 16))
    .filter((cp) => !Number.isNaN(cp));
  return codepoints.length ? String.fromCodePoint(...codepoints) : undefined;
}

function withEmojiGlyphs(html: string): string {
  return html.replace(EMOJI_SPAN_RE, (match, openTag, codeGroup, closeTag) => {
    const glyph = codepointsToGlyph(codeGroup as string);
    return glyph ? `${openTag}${glyph}${closeTag}` : match;
  });
}

// The inverse of codepointsToGlyph — Zulip's own dash-separated-hex
// encoding for a "unicode_emoji" reaction's emoji_code (see
// emoji_to_hex_codepoint in Zulip's zerver/lib/emoji_utils.py, used by
// check_emoji_request in zerver/lib/emoji.py to validate reactions). The
// variation selector (U+FE0F, used to force emoji-style rendering of
// otherwise text-style glyphs) is stripped since Zulip's own
// codepoint_to_name table generally excludes it — keeping it would mean our
// reactions fail to merge with the same emoji reacted via Zulip's native
// web/mobile clients. Each codepoint is zero-padded to 4 hex digits to match
// Zulip's own `f"{ord(c):04x}"` formatting — without it, low codepoints
// (keycap digits, ©, ®, etc.) come out as e.g. "23-20e3" instead of Zulip's
// "0023-20e3" and get rejected as an unrecognized emoji code.
function unicodeEmojiCode(glyph: string): string {
  return Array.from(glyph)
    .map((ch) => ch.codePointAt(0) ?? 0)
    .filter((cp) => cp !== 0xfe0f)
    .map((cp) => cp.toString(16).padStart(4, "0"))
    .join("-");
}

type ReactionGroup = {
  key: string;
  emojiName: string;
  emojiCode: string;
  reactionType: string;
  glyph?: string;
  count: number;
  mine: boolean;
  userNames: string[];
};

function groupReactions(reactions: ZulipReaction[] | undefined, myUserId: number | null, realmUsers: ZulipUser[]): ReactionGroup[] {
  const nameFor = (userId: number) => (userId === myUserId ? "You" : realmUsers.find((u) => u.user_id === userId)?.full_name ?? "Someone");
  const byKey = new Map<string, ReactionGroup>();
  for (const r of reactions ?? []) {
    const key = `${r.reaction_type}:${r.emoji_code}`;
    let group = byKey.get(key);
    if (!group) {
      group = {
        key,
        emojiName: r.emoji_name,
        emojiCode: r.emoji_code,
        reactionType: r.reaction_type,
        glyph: r.reaction_type === "unicode_emoji" ? codepointsToGlyph(r.emoji_code) : undefined,
        count: 0,
        mine: false,
        userNames: [],
      };
      byKey.set(key, group);
    }
    group.count += 1;
    if (r.user_id === myUserId) group.mine = true;
    group.userNames.push(nameFor(r.user_id));
  }
  return [...byKey.values()];
}

function isRealmRelative(value: string): boolean {
  return value.startsWith("/") || isOwnUploadUrl(value);
}

const ATTR_RE = /\s(src|href)="([^"]*)"/g;

function withAbsoluteRealmUrls(html: string): string {
  return html.replace(ATTR_RE, (match, attr, value) => {
    if (!value || !isRealmRelative(value)) return match;
    const resolved = resolveRealmUrl(value);
    return resolved ? ` ${attr}="${resolved}"` : match;
  });
}

type ParsedBody = { html: string; images: { src: string; alt?: string }[] };

function extractOwnUploadImages(html: string): ParsedBody {
  const container = document.createElement("div");
  container.innerHTML = html;
  const images: { src: string; alt?: string }[] = [];
  container.querySelectorAll("img").forEach((img) => {
    const src = img.getAttribute("src");
    if (!src || !isOwnUploadUrl(src)) return;
    images.push({ src, alt: img.getAttribute("alt") || undefined });
    (img.closest("div.message_inline_image") ?? img).remove();
  });
  return { html: container.innerHTML, images };
}

function preprocessContent(html: string): ParsedBody {
  return extractOwnUploadImages(withAbsoluteRealmUrls(withEmojiGlyphs(html)));
}

const MessageBubble: React.FC<{ msg: ZulipMessage; mine: boolean; showSender: boolean }> = ({ msg, mine, showSender }) => {
  const time = dayjs.unix(msg.timestamp);
  const { html: bodyHtml, images } = useMemo(() => preprocessContent(msg.content), [msg.content]);
  const { myUserId, realmUsers, toggleReaction, quoteMessage, setForwardTarget } = useChat();
  const [pickerOpen, setPickerOpen] = useState(false);

  const reactionGroups = useMemo(() => groupReactions(msg.reactions, myUserId, realmUsers), [msg.reactions, myUserId, realmUsers]);
  // Once there's at least one reaction, the add-reaction trigger moves
  // inline into the reactions row (right after the existing pills, always
  // visible alongside them) instead of floating over the message — with no
  // reactions yet there's no row for it to join, so it stays a
  // hover-revealed button in the top-right corner (see &__hover-actions in
  // Chat.less), same as any other per-message hover action.
  const hasReactions = reactionGroups.length > 0;

  const handleEmojiClick = (emojiData: EmojiClickData) => {
    setPickerOpen(false);
    toggleReaction(msg.id, {
      emojiName: emojiData.names?.[0] || emojiData.emoji,
      emojiCode: unicodeEmojiCode(emojiData.emoji),
      reactionType: "unicode_emoji",
    });
  };

  return (
    <div className={`chat-message-row${mine ? " chat-message-row--mine" : ""}`}>
      <div className="chat-message-row__avatar-col">
        {showSender && (
          <Avatar size={32} src={resolveAvatarUrl(msg.avatar_url) || undefined} icon={<UserOutlined />} />
        )}
      </div>
      <div className="chat-message-row__content-col">
        {showSender && (
          <div className="chat-message-row__meta">
            <span className="chat-message-row__sender">{msg.sender_full_name}</span>
            <span className="chat-message-row__time">{time.format("h:mm A")}</span>
          </div>
        )}
        <div
          className="chat-message-row__body"
          dangerouslySetInnerHTML={{ __html: bodyHtml }}
        />
        {images.length > 0 && (
          <Image.PreviewGroup>
            <div className="chat-message-row__images">
              {images.map((img, i) => (
                <Image
                  key={i}
                  src={img.src}
                  alt={img.alt}
                  width={160}
                  style={{ borderRadius: 6, objectFit: "cover", maxHeight: 160 }}
                />
              ))}
            </div>
          </Image.PreviewGroup>
        )}
        {hasReactions && (
          <div className="chat-message-row__reactions">
            {reactionGroups.map((g) => (
              <Tooltip key={g.key} title={g.userNames.join(", ")}>
                <button
                  type="button"
                  className={`chat-reaction-pill${g.mine ? " chat-reaction-pill--mine" : ""}`}
                  onClick={() => toggleReaction(msg.id, { emojiName: g.emojiName, emojiCode: g.emojiCode, reactionType: g.reactionType })}
                >
                  <span>{g.glyph ?? `:${g.emojiName}:`}</span>
                  <span className="chat-reaction-pill__count">{g.count}</span>
                </button>
              </Tooltip>
            ))}
            <EmojiPickerPopover open={pickerOpen} onOpenChange={setPickerOpen} placement="topLeft" onEmojiClick={handleEmojiClick}>
              <Button type="text" size="small" shape="circle" className="chat-message-row__react-btn" icon={<SmileOutlined />} title="Add reaction" />
            </EmojiPickerPopover>
          </div>
        )}
      </div>
      <div className={`chat-message-row__hover-actions${pickerOpen ? " chat-message-row__hover-actions--open" : ""}`}>
        <Button
          type="text"
          size="small"
          shape="circle"
          className="chat-message-row__action-btn"
          icon={<CommentOutlined />}
          title="Quote message"
          onClick={() => quoteMessage(msg)}
        />
        <Button
          type="text"
          size="small"
          shape="circle"
          className="chat-message-row__action-btn"
          icon={<ForwardOutlined />}
          title="Forward message"
          onClick={() => setForwardTarget(msg)}
        />
        {/* Once a first reaction lands, this moves inline into &__reactions
            instead (see hasReactions above) — no duplicate trigger here. */}
        {!hasReactions && (
          <EmojiPickerPopover open={pickerOpen} onOpenChange={setPickerOpen} placement="topRight" onEmojiClick={handleEmojiClick}>
            <Button type="text" size="small" shape="circle" className="chat-message-row__action-btn" icon={<SmileOutlined />} title="Add reaction" />
          </EmojiPickerPopover>
        )}
      </div>
    </div>
  );
};

export default MessageBubble;
